import React from "react";
import { toast } from "sonner";
import { Download, FileSpreadsheet, RefreshCw, User, Truck, MapPin, Building2, Mail, Send, X, CheckCircle2, AlertCircle, Clock, Zap } from "lucide-react";
import { adminApi, API } from "../lib/api";

const MONTHS = [
  "Januar", "Februar", "Marts", "April", "Maj", "Juni",
  "Juli", "August", "September", "Oktober", "November", "December",
];

export default function DriverStatementsPanel() {
  const today = new Date();
  const prev = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const [year, setYear] = React.useState(prev.getFullYear());
  const [month, setMonth] = React.useState(prev.getMonth() + 1);
  const [view, setView] = React.useState("driver"); // "driver" | "subcontractor"
  const [data, setData] = React.useState(null);
  const [scData, setScData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [expanded, setExpanded] = React.useState(null);
  const [emailModal, setEmailModal] = React.useState(null); // { sc, email, message, sending }
  const [bulkModal, setBulkModal] = React.useState(null); // { message, sending, result }
  const [autoConfig, setAutoConfig] = React.useState(null); // scheduler status
  const [autoSaving, setAutoSaving] = React.useState(false);
  const [autoMessage, setAutoMessage] = React.useState("");
  const [autoRunningNow, setAutoRunningNow] = React.useState(false);
  const [billyPushing, setBillyPushing] = React.useState(null); // sc.id of currently pushing

  const loadAutoConfig = React.useCallback(async () => {
    try {
      const { data } = await adminApi().get("/admin/auto-statements/status");
      setAutoConfig(data);
      setAutoMessage(data.custom_message || "");
    } catch { /* silent — feature still works without status */ }
  }, []);

  React.useEffect(() => { if (view === "subcontractor") loadAutoConfig(); }, [view, loadAutoConfig]);

  const toggleAuto = async (nextEnabled) => {
    setAutoSaving(true);
    try {
      await adminApi().post("/admin/auto-statements/toggle", { enabled: nextEnabled, custom_message: autoMessage });
      toast.success(nextEnabled ? "Automatisk afsendelse aktiveret" : "Automatisk afsendelse deaktiveret");
      await loadAutoConfig();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke gemme indstilling");
    } finally {
      setAutoSaving(false);
    }
  };

  const saveAutoMessage = async () => {
    setAutoSaving(true);
    try {
      await adminApi().post("/admin/auto-statements/toggle", { enabled: !!autoConfig?.enabled, custom_message: autoMessage });
      toast.success("Besked gemt");
      await loadAutoConfig();
    } catch {
      toast.error("Kunne ikke gemme besked");
    } finally {
      setAutoSaving(false);
    }
  };

  const runAutoNow = async () => {
    if (!confirm("Send opgørelser for forrige måned til alle vognmænd nu? Dette ignorerer enabled-flaget og kan ikke fortrydes.")) return;
    setAutoRunningNow(true);
    try {
      const { data } = await adminApi().post("/admin/auto-statements/run-now");
      toast.success(`Manuelt kørt: ${data.sent} sendt · ${data.skipped} sprunget · ${data.failed} fejlede`);
      await loadAutoConfig();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Manuel kørsel fejlede");
    } finally {
      setAutoRunningNow(false);
    }
  };

  const pushBilly = async (sc) => {
    setBillyPushing(sc.id);
    try {
      const { data } = await adminApi().post(`/admin/subcontractor-statements/${sc.id}/billy-push`, { year, month });
      toast.success(`Billy.dk udgiftsbilag oprettet (${data.total_dkk?.toLocaleString("da-DK")} kr)`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Billy.dk push fejlede");
    } finally {
      setBillyPushing(null);
    }
  };

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      if (view === "driver") {
        const { data } = await adminApi().get("/admin/driver-statements", { params: { year, month } });
        setData(data);
      } else {
        const { data } = await adminApi().get("/admin/subcontractor-statements", { params: { year, month } });
        setScData(data);
      }
    } catch { toast.error("Kunne ikke hente opgørelser"); }
    finally { setLoading(false); }
  }, [year, month, view]);

  React.useEffect(() => { load(); }, [load]);

  const downloadFile = async (url, filename) => {
    const token = localStorage.getItem("admin_token");
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const u = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(u);
      toast.success("Excel downloadet");
    } catch { toast.error("Eksport fejlede"); }
  };

  const exportXlsx = () => downloadFile(
    `${API}/admin/driver-statements/export?year=${year}&month=${month}`,
    `EMA_Kurer_Chauffor_Opgorelse_${year}-${String(month).padStart(2, "0")}.xlsx`
  );

  const exportSc = (sc) => downloadFile(
    `${API}/admin/subcontractor-statements/${sc.id}/export?year=${year}&month=${month}`,
    `Opgorelse_${(sc.company_name || "Vognmand").replace(/[^a-zA-Z0-9]/g, "_")}_${year}-${String(month).padStart(2, "0")}.xlsx`
  );

  const openEmailModal = (sc, stops) => {
    if (!sc?.id) return;
    if (!stops) { toast.error("Ingen leveringer at sende for denne periode"); return; }
    setEmailModal({
      sc,
      email: sc.email || "",
      message: "",
      sending: false,
    });
  };

  const sendEmail = async () => {
    if (!emailModal) return;
    const { sc, email, message } = emailModal;
    if (!email || !email.includes("@")) { toast.error("Ugyldig email-adresse"); return; }
    setEmailModal((m) => ({ ...m, sending: true }));
    try {
      const { data } = await adminApi().post(`/admin/subcontractor-statements/${sc.id}/email`, {
        year, month, to_email: email, custom_message: message || null,
      });
      toast.success(`Opgørelse sendt til ${data.sent_to} (${data.total_dkk?.toLocaleString("da-DK")} kr)`);
      setEmailModal(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Email-afsendelse fejlede");
      setEmailModal((m) => m ? { ...m, sending: false } : null);
    }
  };

  const openBulkModal = () => {
    const list = scData?.subcontractors || [];
    const eligible = list.filter((e) => !e.subcontractor.is_inhouse && e.subcontractor.email && e.totals.stops > 0);
    if (eligible.length === 0) { toast.error("Ingen vognmænd med email + leveringer at sende til"); return; }
    setBulkModal({ message: "", sending: false, result: null, eligibleCount: eligible.length });
  };

  const sendBulk = async () => {
    if (!bulkModal) return;
    setBulkModal((m) => ({ ...m, sending: true }));
    try {
      const { data } = await adminApi().post(`/admin/subcontractor-statements/email-all`, {
        year, month, custom_message: bulkModal.message || null,
      });
      setBulkModal((m) => ({ ...m, sending: false, result: data }));
      toast.success(`${data.sent} sendt, ${data.skipped} sprunget over, ${data.failed} fejlede`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk-afsendelse fejlede");
      setBulkModal((m) => m ? { ...m, sending: false } : null);
    }
  };

  if (loading && !data && !scData) return <div className="text-brand-text2" data-testid="statements-loading">Indlæser opgørelser...</div>;

  const t = data?.totals || {};
  const yearOptions = Array.from({ length: 4 }, (_, i) => today.getFullYear() - i);
  const scList = scData?.subcontractors || [];

  return (
    <div className="space-y-6" data-testid="statements-panel">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="kicker">Opgørelse · Løn & fakturering</div>
          <h3 className="font-display font-semibold text-2xl text-brand-dark mt-2">
            {view === "driver" ? "Chauffør-opgørelse" : "Vognmand-opgørelse"}
          </h3>
          <p className="text-sm text-brand-text2 mt-1">
            {view === "driver"
              ? "Månedsoversigt per chauffør med kørte km og leveret omsætning."
              : "Månedsoversigt per vognmand — eksportér Excel som de kan opkræve dig for."}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-end">
          {/* View toggle */}
          <div className="inline-flex border border-brand" role="group">
            <button
              onClick={() => setView("driver")}
              className={`px-3 py-2 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5 ${view === "driver" ? "bg-brand-dark text-white" : "bg-white text-brand-dark hover:bg-brand-bg2"}`}
              data-testid="view-driver"
            ><User size={12} /> Pr. chauffør</button>
            <button
              onClick={() => setView("subcontractor")}
              className={`px-3 py-2 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5 ${view === "subcontractor" ? "bg-brand-dark text-white" : "bg-white text-brand-dark hover:bg-brand-bg2"}`}
              data-testid="view-subcontractor"
            ><Building2 size={12} /> Pr. vognmand</button>
          </div>
          <div>
            <label className="label-flat text-xs">Måned</label>
            <select value={month} onChange={(e) => setMonth(parseInt(e.target.value, 10))} className="input-flat py-2" data-testid="statements-month">
              {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="label-flat text-xs">År</label>
            <select value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))} className="input-flat py-2" data-testid="statements-year">
              {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <button onClick={load} className="btn-secondary inline-flex items-center gap-2" data-testid="statements-refresh">
            <RefreshCw size={14} /> Opdater
          </button>
          {view === "driver" && (
            <button onClick={exportXlsx} className="btn-primary inline-flex items-center gap-2" disabled={!t.drivers} data-testid="statements-export">
              <Download size={14} /> Excel
            </button>
          )}
          {view === "subcontractor" && (
            <button onClick={openBulkModal} className="btn-primary inline-flex items-center gap-2" disabled={scList.length === 0} data-testid="bulk-email-btn">
              <Send size={14} /> Send til alle
            </button>
          )}
        </div>
      </div>

      {view === "driver" ? (
        <>
          {/* Totals strip */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="bg-white border border-brand p-4" data-testid="stat-drivers">
              <div className="text-xs uppercase tracking-widest text-brand-text2">Chauffører</div>
              <div className="font-display font-bold text-3xl text-brand-dark mt-1">{t.drivers ?? 0}</div>
            </div>
            <div className="bg-white border border-brand p-4" data-testid="stat-stops">
              <div className="text-xs uppercase tracking-widest text-brand-text2">Leveringer</div>
              <div className="font-display font-bold text-3xl text-brand-dark mt-1">{t.stops ?? 0}</div>
            </div>
            <div className="bg-white border border-brand p-4" data-testid="stat-km">
              <div className="text-xs uppercase tracking-widest text-brand-text2">Total km</div>
              <div className="font-display font-bold text-3xl text-brand-dark mt-1">{(t.km ?? 0).toLocaleString("da-DK")}</div>
            </div>
            <div className="bg-brand-dark text-white p-4" data-testid="stat-revenue">
              <div className="text-xs uppercase tracking-widest text-white/70">Total omsætning</div>
              <div className="font-display font-bold text-3xl mt-1">{(t.revenue_dkk ?? 0).toLocaleString("da-DK")} kr</div>
            </div>
          </div>

          {(!data?.statements || data.statements.length === 0) ? (
            <div className="bg-white border border-brand p-10 text-center" data-testid="statements-empty">
              <FileSpreadsheet size={32} className="mx-auto text-brand-text2" />
              <div className="font-display font-semibold text-brand-dark mt-3">Ingen leveringer i {MONTHS[month - 1]} {year}</div>
              <p className="text-sm text-brand-text2 mt-1">Når dine chauffører leverer pakker, vises deres opgørelse her.</p>
            </div>
          ) : (
            <div className="bg-white border border-brand overflow-x-auto" data-testid="statements-table">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-brand-text2 uppercase tracking-widest border-b border-brand bg-brand-bg2">
                  <tr>
                    <th className="px-4 py-3">Chauffør</th>
                    <th className="px-4 py-3 text-right">Stops</th>
                    <th className="px-4 py-3 text-right">Total km</th>
                    <th className="px-4 py-3 text-right">Total omsætning</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.statements.map((st) => (
                    <React.Fragment key={st.driver_id}>
                      <tr className="border-t border-brand align-top hover:bg-brand-bg2/40">
                        <td className="px-4 py-3">
                          <div className="font-semibold text-brand-dark flex items-center gap-2"><User size={14} className="text-brand-red" />{st.driver_name}</div>
                          <div className="text-xs text-brand-text2 mt-0.5">{st.driver_email}</div>
                        </td>
                        <td className="px-4 py-3 text-right font-mono">{st.stops}</td>
                        <td className="px-4 py-3 text-right font-mono">{st.total_km.toLocaleString("da-DK")} km</td>
                        <td className="px-4 py-3 text-right font-display font-bold text-brand-red">{st.total_revenue_dkk.toLocaleString("da-DK")} kr</td>
                        <td className="px-4 py-3 text-right">
                          <button onClick={() => setExpanded(expanded === st.driver_id ? null : st.driver_id)} className="text-xs text-brand-red font-semibold hover:underline" disabled={!st.rows?.length} data-testid={`statement-toggle-${st.driver_id}`}>
                            {expanded === st.driver_id ? "Skjul" : `Vis ${st.rows.length} stops`}
                          </button>
                        </td>
                      </tr>
                      {expanded === st.driver_id && st.rows?.length > 0 && (
                        <tr className="border-t border-brand bg-brand-bg2">
                          <td colSpan={5} className="px-4 py-4">
                            <table className="w-full text-xs">
                              <thead className="text-brand-text2">
                                <tr>
                                  <th className="text-left py-1">Dato</th>
                                  <th className="text-left py-1">Booking</th>
                                  <th className="text-left py-1">Type</th>
                                  <th className="text-left py-1">Rute</th>
                                  <th className="text-right py-1">Km</th>
                                  <th className="text-right py-1">Beløb</th>
                                </tr>
                              </thead>
                              <tbody>
                                {st.rows.map((r, i) => (
                                  <tr key={i} className="border-t border-brand/30">
                                    <td className="py-1.5">{(r.delivered_at || "").slice(0, 10)}</td>
                                    <td className="py-1.5 font-mono">{r.booking_number}</td>
                                    <td className="py-1.5">{r.delivery_type}</td>
                                    <td className="py-1.5 flex items-center gap-1"><MapPin size={10} className="text-brand-red" />{r.pickup_city} → {r.delivery_city}</td>
                                    <td className="py-1.5 text-right font-mono">{r.distance_km} km</td>
                                    <td className="py-1.5 text-right font-mono">{r.total_dkk.toLocaleString("da-DK")} kr</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        // Vognmand-view
        <>
          {/* Auto-statements scheduler panel */}
          {autoConfig && (
            <div className={`border ${autoConfig.enabled ? "border-emerald-300 bg-emerald-50" : "border-brand bg-white"} p-5`} data-testid="auto-statements-panel">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className={`mt-1 ${autoConfig.enabled ? "text-emerald-700" : "text-brand-text2"}`}>
                    <Clock size={22} />
                  </div>
                  <div>
                    <div className="font-display font-semibold text-brand-dark text-lg flex items-center gap-2">
                      Automatisk månedlig afsendelse
                      {autoConfig.enabled
                        ? <span className="text-xs bg-emerald-600 text-white px-2 py-0.5 font-bold uppercase tracking-wider">Aktiv</span>
                        : <span className="text-xs bg-slate-300 text-slate-800 px-2 py-0.5 font-bold uppercase tracking-wider">Deaktiveret</span>}
                    </div>
                    <div className="text-sm text-brand-text2 mt-1">{autoConfig.schedule_description}</div>
                    {autoConfig.next_run_at && (
                      <div className="text-xs text-brand-text2 mt-2">
                        <strong>Næste kørsel:</strong> {new Date(autoConfig.next_run_at).toLocaleString("da-DK", { dateStyle: "full", timeStyle: "short", timeZone: "Europe/Copenhagen" })}
                      </div>
                    )}
                    {autoConfig.last_run_at && (
                      <div className="text-xs text-brand-text2 mt-1" data-testid="auto-last-run">
                        <strong>Sidste kørsel:</strong> {new Date(autoConfig.last_run_at).toLocaleString("da-DK", { dateStyle: "medium", timeStyle: "short", timeZone: "Europe/Copenhagen" })} ({autoConfig.last_run_period || "—"}) · status: <span className={autoConfig.last_run_status?.includes("ok") ? "text-emerald-700 font-bold" : "text-rose-700 font-bold"}>{autoConfig.last_run_status}</span>
                        {autoConfig.last_run_result && (
                          <span> · {autoConfig.last_run_result.sent} sendt / {autoConfig.last_run_result.skipped} sprunget / {autoConfig.last_run_result.failed} fejlede</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={runAutoNow}
                    disabled={autoRunningNow || autoSaving}
                    className="btn-secondary inline-flex items-center gap-2 text-xs"
                    data-testid="auto-run-now"
                    title="Test scheduler manuelt — sender forrige måneds opgørelser nu"
                  >
                    {autoRunningNow ? <><RefreshCw size={12} className="animate-spin" /> Kører...</> : <><Zap size={12} /> Test nu</>}
                  </button>
                  <button
                    onClick={() => toggleAuto(!autoConfig.enabled)}
                    disabled={autoSaving}
                    className={autoConfig.enabled ? "btn-secondary inline-flex items-center gap-2" : "btn-primary inline-flex items-center gap-2"}
                    data-testid="auto-toggle"
                  >
                    {autoSaving ? <RefreshCw size={14} className="animate-spin" /> : <Clock size={14} />}
                    {autoConfig.enabled ? "Slå fra" : "Slå til"}
                  </button>
                </div>
              </div>
              <div className="mt-4 border-t border-emerald-200 pt-3">
                <label className="label-flat text-xs">Standard-besked til vognmænd (valgfri)</label>
                <div className="flex gap-2 mt-1">
                  <input
                    value={autoMessage}
                    onChange={(e) => setAutoMessage(e.target.value)}
                    placeholder="F.eks. 'Tak for samarbejdet i denne måned!'"
                    className="input-flat flex-1"
                    disabled={autoSaving}
                    data-testid="auto-message"
                  />
                  <button onClick={saveAutoMessage} disabled={autoSaving} className="btn-secondary text-xs" data-testid="auto-message-save">
                    Gem besked
                  </button>
                </div>
              </div>
            </div>
          )}

          {scList.length === 0 ? (
            <div className="bg-white border border-brand p-10 text-center" data-testid="sc-statements-empty">
              <Building2 size={32} className="mx-auto text-brand-text2" />
              <div className="font-display font-semibold text-brand-dark mt-3">Ingen vognmænd oprettet</div>
              <p className="text-sm text-brand-text2 mt-1">Opret vognmænd under "Vognmænd"-tabben for at se opgørelser her.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {scList.map((entry) => (
                <div key={entry.subcontractor.id || "inhouse"} className="bg-white border border-brand p-5" data-testid={`sc-card-${entry.subcontractor.id || "inhouse"}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="font-display font-semibold text-lg text-brand-dark flex items-center gap-2">
                        <Building2 size={18} className="text-brand-red" />
                        {entry.subcontractor.company_name}
                        {entry.subcontractor.is_inhouse && <span className="text-xs bg-slate-200 px-2 py-0.5 font-bold">EGNE</span>}
                      </div>
                      <div className="text-xs text-brand-text2 mt-1">
                        {entry.subcontractor.cvr && <span>CVR {entry.subcontractor.cvr} · </span>}
                        {entry.totals.drivers} chauffører · {entry.totals.stops} leveringer · {entry.totals.km} km
                      </div>
                    </div>
                    <div className="flex items-end gap-3">
                      <div className="text-right">
                        <div className="text-xs uppercase tracking-widest text-brand-text2">Total at opkræve</div>
                        <div className="font-display font-bold text-2xl text-brand-red">{entry.totals.revenue_dkk.toLocaleString("da-DK")} kr</div>
                      </div>
                      {!entry.subcontractor.is_inhouse && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => exportSc(entry.subcontractor)}
                            className="btn-secondary inline-flex items-center gap-2"
                            disabled={entry.totals.stops === 0}
                            data-testid={`sc-export-${entry.subcontractor.id}`}
                          >
                            <Download size={14} /> Excel
                          </button>
                          <button
                            onClick={() => openEmailModal(entry.subcontractor, entry.totals.stops)}
                            className="btn-primary inline-flex items-center gap-2"
                            disabled={entry.totals.stops === 0 || !entry.subcontractor.email}
                            title={!entry.subcontractor.email ? "Vognmanden har ingen email registreret" : ""}
                            data-testid={`sc-email-${entry.subcontractor.id}`}
                          >
                            <Mail size={14} /> Send email
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  {entry.subcontractor.last_statement_sent_at && entry.subcontractor.last_statement_period === `${year}-${String(month).padStart(2, "0")}` && (
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <div className="inline-flex items-center gap-2 text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-1.5 font-medium" data-testid={`sc-last-sent-${entry.subcontractor.id}`}>
                        <CheckCircle2 size={12} /> Sendt {new Date(entry.subcontractor.last_statement_sent_at).toLocaleString("da-DK", { dateStyle: "medium", timeStyle: "short" })} til {entry.subcontractor.last_statement_sent_to || entry.subcontractor.email}
                      </div>
                      {entry.subcontractor.last_billy_bill_id && entry.subcontractor.last_billy_period === `${year}-${String(month).padStart(2, "0")}` ? (
                        <div className="inline-flex items-center gap-2 text-xs bg-indigo-50 border border-indigo-200 text-indigo-800 px-3 py-1.5 font-medium" data-testid={`sc-billy-${entry.subcontractor.id}`} title={`Billy bill id: ${entry.subcontractor.last_billy_bill_id}`}>
                          <FileSpreadsheet size={12} /> Billy.dk ✓ udgiftsbilag bogført
                        </div>
                      ) : (
                        <button
                          onClick={() => pushBilly(entry.subcontractor)}
                          disabled={entry.totals.stops === 0 || billyPushing === entry.subcontractor.id}
                          className="inline-flex items-center gap-2 text-xs bg-white border border-indigo-300 text-indigo-700 hover:bg-indigo-50 px-3 py-1.5 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                          data-testid={`sc-billy-push-${entry.subcontractor.id}`}
                          title="Opret udgiftsbilag i Billy.dk for denne periode"
                        >
                          {billyPushing === entry.subcontractor.id ? <RefreshCw size={12} className="animate-spin" /> : <FileSpreadsheet size={12} />}
                          Push til Billy.dk
                        </button>
                      )}
                    </div>
                  )}
                  {entry.drivers.length > 0 && (
                    <div className="mt-4 border-t border-brand pt-3">
                      <div className="text-xs uppercase tracking-widest text-brand-text2 mb-2">Chauffører i denne periode</div>
                      <table className="w-full text-sm">
                        <thead className="text-xs text-brand-text2 text-left">
                          <tr>
                            <th className="py-1">Navn</th>
                            <th className="py-1 text-right">Stops</th>
                            <th className="py-1 text-right">Km</th>
                            <th className="py-1 text-right">Omsætning</th>
                          </tr>
                        </thead>
                        <tbody>
                          {entry.drivers.map((d) => (
                            <tr key={d.driver_id} className="border-t border-slate-100">
                              <td className="py-1.5">{d.driver_name}</td>
                              <td className="py-1.5 text-right font-mono">{d.stops}</td>
                              <td className="py-1.5 text-right font-mono">{d.total_km} km</td>
                              <td className="py-1.5 text-right font-mono">{d.total_revenue_dkk.toLocaleString("da-DK")} kr</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <div className="bg-slate-50 border border-slate-200 p-4 text-xs text-brand-text2 flex items-start gap-3">
        <Truck size={16} className="flex-shrink-0 mt-0.5 text-brand-red" />
        <div>
          <strong className="text-brand-dark">Tip:</strong> {view === "driver"
            ? "Excel-eksporten indeholder en oversigtsfane + én fane per chauffør. Brug den til løn-udbetaling eller intern fakturering."
            : "Klik 'Send email' for at sende opgørelsen direkte til vognmanden, eller 'Excel' for at downloade og sende manuelt. Brug 'Send til alle' for at sende til alle vognmænd på én gang."}
        </div>
      </div>

      {/* Email Single Modal */}
      {emailModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="email-modal" onClick={(e) => { if (e.target === e.currentTarget && !emailModal.sending) setEmailModal(null); }}>
          <div className="bg-white max-w-lg w-full border border-brand">
            <div className="flex items-center justify-between px-5 py-4 border-b border-brand bg-brand-dark text-white">
              <div className="flex items-center gap-2"><Mail size={16} /> <span className="font-display font-semibold">Send månedsopgørelse</span></div>
              <button onClick={() => setEmailModal(null)} disabled={emailModal.sending} className="text-white/70 hover:text-white" data-testid="email-modal-close"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-brand-text2">Vognmand</div>
                <div className="font-semibold text-brand-dark mt-1">{emailModal.sc.company_name}</div>
                <div className="text-xs text-brand-text2">{MONTHS[month - 1]} {year} · Excel-fil vedhæftet automatisk</div>
              </div>
              <div>
                <label className="label-flat">Modtager-email</label>
                <input
                  type="email"
                  value={emailModal.email}
                  onChange={(e) => setEmailModal((m) => ({ ...m, email: e.target.value }))}
                  className="input-flat w-full"
                  disabled={emailModal.sending}
                  data-testid="email-modal-recipient"
                />
              </div>
              <div>
                <label className="label-flat">Personlig besked (valgfri)</label>
                <textarea
                  value={emailModal.message}
                  onChange={(e) => setEmailModal((m) => ({ ...m, message: e.target.value }))}
                  className="input-flat w-full min-h-[90px]"
                  placeholder="F.eks. 'Husk at sende faktura inden d. 28.'"
                  disabled={emailModal.sending}
                  data-testid="email-modal-message"
                />
              </div>
            </div>
            <div className="px-5 py-4 border-t border-brand flex justify-end gap-2 bg-brand-bg2">
              <button onClick={() => setEmailModal(null)} disabled={emailModal.sending} className="btn-secondary" data-testid="email-modal-cancel">Annullér</button>
              <button onClick={sendEmail} disabled={emailModal.sending || !emailModal.email} className="btn-primary inline-flex items-center gap-2" data-testid="email-modal-send">
                {emailModal.sending ? <><RefreshCw size={14} className="animate-spin" /> Sender...</> : <><Send size={14} /> Afsend nu</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Bulk Modal */}
      {bulkModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="bulk-email-modal" onClick={(e) => { if (e.target === e.currentTarget && !bulkModal.sending) setBulkModal(null); }}>
          <div className="bg-white max-w-xl w-full border border-brand max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-brand bg-brand-dark text-white">
              <div className="flex items-center gap-2"><Send size={16} /> <span className="font-display font-semibold">Send til alle vognmænd</span></div>
              <button onClick={() => setBulkModal(null)} disabled={bulkModal.sending} className="text-white/70 hover:text-white" data-testid="bulk-modal-close"><X size={18} /></button>
            </div>
            {!bulkModal.result ? (
              <>
                <div className="p-5 space-y-4">
                  <div className="bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 flex items-start gap-2">
                    <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                    <div>Sender opgørelse for <strong>{MONTHS[month - 1]} {year}</strong> til <strong>{bulkModal.eligibleCount}</strong> vognmænd med registreret email og leveringer.</div>
                  </div>
                  <div>
                    <label className="label-flat">Fælles besked (valgfri)</label>
                    <textarea
                      value={bulkModal.message}
                      onChange={(e) => setBulkModal((m) => ({ ...m, message: e.target.value }))}
                      className="input-flat w-full min-h-[100px]"
                      placeholder="Beskeden vises i alle emails. F.eks. 'Tak for samarbejdet i denne måned!'"
                      disabled={bulkModal.sending}
                      data-testid="bulk-modal-message"
                    />
                  </div>
                </div>
                <div className="px-5 py-4 border-t border-brand flex justify-end gap-2 bg-brand-bg2">
                  <button onClick={() => setBulkModal(null)} disabled={bulkModal.sending} className="btn-secondary" data-testid="bulk-modal-cancel">Annullér</button>
                  <button onClick={sendBulk} disabled={bulkModal.sending} className="btn-primary inline-flex items-center gap-2" data-testid="bulk-modal-send">
                    {bulkModal.sending ? <><RefreshCw size={14} className="animate-spin" /> Sender {bulkModal.eligibleCount} emails...</> : <><Send size={14} /> Send til {bulkModal.eligibleCount} vognmænd</>}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="p-5 space-y-4" data-testid="bulk-modal-result">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-emerald-50 border border-emerald-200 p-3 text-center">
                      <div className="text-2xl font-bold text-emerald-700">{bulkModal.result.sent}</div>
                      <div className="text-xs text-emerald-800 uppercase tracking-widest mt-1">Sendt</div>
                    </div>
                    <div className="bg-amber-50 border border-amber-200 p-3 text-center">
                      <div className="text-2xl font-bold text-amber-700">{bulkModal.result.skipped}</div>
                      <div className="text-xs text-amber-800 uppercase tracking-widest mt-1">Sprunget over</div>
                    </div>
                    <div className="bg-rose-50 border border-rose-200 p-3 text-center">
                      <div className="text-2xl font-bold text-rose-700">{bulkModal.result.failed}</div>
                      <div className="text-xs text-rose-800 uppercase tracking-widest mt-1">Fejlede</div>
                    </div>
                  </div>
                  <div className="max-h-64 overflow-y-auto border border-brand">
                    <table className="w-full text-xs">
                      <thead className="bg-brand-bg2 sticky top-0">
                        <tr className="text-left text-brand-text2 uppercase tracking-widest">
                          <th className="px-3 py-2">Vognmand</th>
                          <th className="px-3 py-2">Status</th>
                          <th className="px-3 py-2">Detalje</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bulkModal.result.results.map((r, i) => (
                          <tr key={i} className="border-t border-brand/30">
                            <td className="px-3 py-2 font-medium">{r.company}</td>
                            <td className="px-3 py-2">
                              {r.status === "sent" && <span className="text-emerald-700 font-bold uppercase">Sendt</span>}
                              {r.status === "skipped" && <span className="text-amber-700 font-bold uppercase">Sprunget</span>}
                              {r.status === "failed" && <span className="text-rose-700 font-bold uppercase">Fejlet</span>}
                            </td>
                            <td className="px-3 py-2 text-brand-text2">
                              {r.status === "sent" ? `${r.to} · ${r.total_dkk?.toLocaleString("da-DK")} kr` : r.reason}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="px-5 py-4 border-t border-brand flex justify-end gap-2 bg-brand-bg2">
                  <button onClick={() => setBulkModal(null)} className="btn-primary" data-testid="bulk-modal-done">Luk</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
